import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import '../../App.css';
import ActivityForm, { IActivityFormData } from '../../components/forms/ActivityForm';
import ActivityCreationWizard from '../../components/ActivityCreationWizard';
import { translateError } from '../../utils/errorMessages';

/**
 * ActivityCreatePage — defaults to the multi-step Activity Creation Wizard
 * (Subject Separation — Phase 2). A "Quick create (no content)" link switches
 * to the legacy simple form that creates an empty activity.
 */
const ActivityCreatePage: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // `?quick=1` forces the legacy simple form; default is the wizard.
  const isQuick = searchParams.get('quick') === '1';

  const submitHandler = async (data: IActivityFormData) => {
    setLoading(true);
    setError(null);

    try {
      await api.activities.create({
        name: data.name,
        description: data.description,
        visibility: data.visibility,
        joinPolicy: data.joinPolicy,
        sortOrder: data.sortOrder,
        coverImage: data.coverImage || undefined,
        bankAccountId: data.bankAccountId,
        groupIds: data.groupIds,
        templateIds: data.templateIds,
      });
      setLoading(false);
      addToast(t('activities.created'), 'success');
      navigate('/admin/activities');
    } catch (err: any) {
      const errorMessage = translateError(err, t);
      setError(errorMessage);
      addToast(t('common.error') + ': ' + errorMessage, 'error');
      setLoading(false);
    }
  };

  if (isQuick) {
    return (
      <div className="admin-form-page">
        <h2>{t('activities.create')}</h2>
        {error && <p className="error-message">{t('common.error')}: {error}</p>}
        <ActivityForm
          onSubmit={submitHandler}
          loading={loading}
          submitButtonText={t('activities.createButton')}
        />
        <Link to="/admin/activities" className="form-link">{t('activities.goBack')}</Link>
        <p style={{ marginTop: '1rem' }}>
          <button
            type="button"
            onClick={() => setSearchParams({})}
            style={{ background: 'none', border: 'none', color: 'var(--orange-primary)', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
          >
            {t('activity.wizard.title')}
          </button>
        </p>
      </div>
    );
  }

  return (
    <div className="admin-form-page">
      <h2>{t('activity.wizard.title')}</h2>
      <ActivityCreationWizard />
      <p style={{ marginTop: '1rem' }}>
        <button
          type="button"
          onClick={() => setSearchParams({ quick: '1' })}
          style={{ background: 'none', border: 'none', color: 'var(--accent, #3498db)', cursor: 'pointer', padding: 0 }}
        >
          {t('activity.wizard.quickCreateLink')}
        </button>
      </p>
      <Link to="/admin/activities" className="form-link">{t('activities.goBack')}</Link>
    </div>
  );
};

export default ActivityCreatePage;
